import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ExternalLink,
  Loader2,
  Maximize2,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { anatomyApi, type AnatomyResource } from "@/features/anatomy/anatomy.api";
import { seedTriageSession } from "@/lib/triage-bridge";
import { cn } from "@/lib/utils";

/** Un recurso es self-hosted (mismo origen) si su embed_url es relativa. */
const isSelfHosted = (r: AnatomyResource | undefined) => !!r && r.embed_url.startsWith("/");

export function AnatomyPanel({ compact = false }: { compact?: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["anatomy-resources"],
    queryFn: anatomyApi.resources,
  });
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  // Para el visor self-hosted: sembramos la sesión SSO ANTES de montar el iframe
  // (si no, hay una race donde el triage embebido bootea sin token, rebota a login
  // y pierde el ?mode=grafo). Solo renderizamos el iframe cuando `seeded` es true.
  const [seeded, setSeeded] = useState(false);

  const active: AnatomyResource | undefined =
    data?.find((r) => r.slug === activeSlug) ?? data?.[0];

  // El visor self-hosted (mismo origen) siempre carga → no aplica el fallback.
  // Antes de embeberlo, sembramos la sesión SSO para que el triage no muestre login.
  // Los iframes externos sí pueden no disparar onLoad (404 / X-Frame-Options): a los
  // ~7s mostramos un fallback con "Abrir en pestaña" en vez del error crudo (B2).
  useEffect(() => {
    setIframeFailed(false);
    setSeeded(false);
    if (!active) return;
    if (isSelfHosted(active)) {
      seedTriageSession();
      setSeeded(true); // recién ahora el token está en localStorage → montar iframe
      return; // sin timeout de fallo
    }
    setSeeded(true);
    const t = setTimeout(() => setIframeFailed(true), 7000);
    return () => clearTimeout(t);
  }, [active?.slug]);

  return (
    <div className={cn("space-y-3", fullscreen && "fixed inset-0 z-50 bg-[var(--color-base)] p-6")}>
      {!compact && (
        <header className="space-y-1">
          <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold">
            <ShieldCheck size={14} className="text-[var(--color-blue-soft)]" />
            Anatomía 3D
          </div>
          <p className="text-[11.5px] text-[var(--color-text-muted)]">
            Visor 3D propio (siempre disponible) + atlas open-source como referencia.
          </p>
        </header>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
          <Loader2 size={14} className="animate-spin" /> Cargando recursos...
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-[var(--color-critical)]/30 bg-[var(--color-critical)]/10 px-3 py-2 text-[12.5px] text-[var(--color-critical)]">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> No se pudieron cargar los recursos de anatomía.
        </div>
      )}

      {data && data.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {data.map((r) => (
              <button
                key={r.slug}
                type="button"
                onClick={() => setActiveSlug(r.slug)}
                className={cn(
                  "rounded-[var(--radius-md)] border px-3 py-1.5 text-[11px] font-semibold transition-colors",
                  (activeSlug ?? data[0]?.slug) === r.slug
                    ? "border-[var(--color-blue)]/50 bg-[var(--color-blue)]/10 text-[var(--color-text-primary)]"
                    : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-blue)]/30",
                )}
              >
                {r.title.split(" (")[0]}
              </button>
            ))}
          </div>

          {active && (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="gold" size="sm">
                    {active.license}
                  </Badge>
                  <span className="truncate font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
                    {active.provider.split(" — ")[0]}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFullscreen((f) => !f)}
                  >
                    <Maximize2 size={12} />
                    {fullscreen ? "Salir" : "Fullscreen"}
                  </Button>
                  <a href={active.embed_url} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink size={12} />
                      Abrir en tab
                    </Button>
                  </a>
                </div>
              </div>

              <div
                className={cn(
                  "relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-2)]",
                  fullscreen
                    ? "h-[calc(100vh-160px)]"
                    : compact
                      ? "h-[60vh] sm:h-[360px]"
                      : "h-[60vh] sm:h-[520px]",
                )}
              >
                {iframeFailed ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                    <p className="text-[13px] text-[var(--color-text-secondary)]">
                      No se pudo cargar el visor embebido de <b>{active.title}</b>.
                    </p>
                    <a
                      href={active.embed_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-strong)] px-3 py-2 text-[13px] font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
                    >
                      <ExternalLink size={14} /> Abrir en una pestaña nueva
                    </a>
                    <p className="text-[11px] text-[var(--color-text-muted)]">
                      O elige otro atlas en las pestañas de arriba.
                    </p>
                  </div>
                ) : !seeded ? (
                  <div className="flex h-full items-center justify-center text-[var(--color-text-muted)]">
                    <Loader2 size={16} className="animate-spin" />
                  </div>
                ) : (
                  <iframe
                    key={active.slug}
                    src={active.embed_url}
                    title={active.title}
                    className="h-full w-full border-0"
                    loading="lazy"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    referrerPolicy="no-referrer"
                    onLoad={() => setIframeFailed(false)}
                  />
                )}
              </div>

              {!compact && (
                <p className="text-[12px] text-[var(--color-text-secondary)]">
                  {active.description}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
